import React, { useState, useCallback } from 'react';

import SubMenu from './SubMenu';
import TopLevelNav from './TopLevelNav';
import { NavItem, NavState } from './interfaces';
import { ComponentProps } from './props';
import * as Styled from './styled';

const EMPTY_NAV_STATE: NavState = {
  id: '',
  items: []
};

const NavBar: React.FC<ComponentProps> = ({ items, headerHeight, appElement }) => {
  /**
   * leftColNav controls the first-level navigation menu (sub-nav)
   */
  const [firstColSubNav, setFirstColSubNav] = useState<NavState>(EMPTY_NAV_STATE);
  /**
   * middleColNav controls the second-level navigation menu (sub-nav)
   */
  const [secondColSubNav, setSecondColSubNav] = useState<NavState>(EMPTY_NAV_STATE);
  /**
   * rightColNav controls the thrid-level navigation menu (sub-nav)
   */
  const [thirdColSubNav, setThirdColSubNav] = useState<NavState>(EMPTY_NAV_STATE);

  /**
   * Event when any of the sub-menus are closed
   */
  const onSubMenuClose = useCallback(() => {
    setFirstColSubNav(EMPTY_NAV_STATE);
    setSecondColSubNav(EMPTY_NAV_STATE);
    setThirdColSubNav(EMPTY_NAV_STATE);
  }, [setFirstColSubNav, setSecondColSubNav, setThirdColSubNav]);

  const onTopMenuItemClick = useCallback(
    (selectedItem: NavItem) => {
      const { id, subItems = [] } = selectedItem;
      // User selected the same top level nav => close the fist sub-menu link
      if (id === firstColSubNav.id) {
        setFirstColSubNav(EMPTY_NAV_STATE);
      } else {
        setFirstColSubNav({
          id,
          items: subItems
        });
      }
      setSecondColSubNav(EMPTY_NAV_STATE);
      setThirdColSubNav(EMPTY_NAV_STATE);
    },
    [firstColSubNav, setFirstColSubNav, setSecondColSubNav, setThirdColSubNav]
  );

  /**
   * Event handler for when a 1st level item in the sub menu gets selected
   * @param item selected 1st level sub menu item
   */
  const onFirstLevelColSelect = useCallback(
    (item: NavItem) => {
      // Flag the second sub menu items with the given 1st level items
      setSecondColSubNav({
        id: item.id,
        items: item.subItems ?? []
      });
      // reset the third level sub menu items to an empty list until an item is selected from the above subItems collection and gets added in the below "onSecondLevelColSelect" method
      setThirdColSubNav(EMPTY_NAV_STATE);
    },
    [setSecondColSubNav, setThirdColSubNav]
  );

  /**
   * Event handler for when a 2nd level item in the sub menu gets selected
   * @param item selected 2nd level sub menu item
   */
  const onSecondLevelColSelect = useCallback(
    (item: NavItem) => {
      setThirdColSubNav({
        id: item.id,
        items: item.subItems ?? []
      });
    },
    [setThirdColSubNav]
  );
  return (
    <Styled.Wrapper>
      <TopLevelNav activeId={firstColSubNav.id} items={items} onClick={onTopMenuItemClick} />
      <SubMenu
        firstColNav={firstColSubNav}
        secondColNav={secondColSubNav}
        thirdColNav={thirdColSubNav}
        onFirstLevelColSelect={onFirstLevelColSelect}
        onSecondLevelColSelect={onSecondLevelColSelect}
        onClose={onSubMenuClose}
        headerHeight={headerHeight}
        appElement={appElement}
      />
    </Styled.Wrapper>
  );
};

export default NavBar;
